import {Injectable, Injector} from '@angular/core';
import {BehaviorSubject, Observable, switchMap} from 'rxjs';
import {
    CustomerDto,
    PagedResultDtoOfCustomerDto,
    PagedResultDtoOfUserListDto,
    UserListDto,
    WebShopServiceProxy,
    UserServiceProxy,
    CheckIfUserExistInputDto,
    ICheckIfUserExistInputDto,
    AddUserToCustomerInputDto,
    IAddUserToCustomerInputDto,
    CreateVirtualBankUserInputDtoWithId,
    ICreateVirtualBankUserInputDtoWithId,
    VirtualBankAccountDto,
    CreateVirtualBankUserInputDto,
    ICreateVirtualBankUserInputDto,
    CheckUserIfExistOutputDto,
    VirtualBankServiceProxy,
    CreateOrUpdateVirtualBankDto,
    ICreateOrUpdateVirtualBankDto
} from '@shared/service-proxies/service-proxies';
import {tap} from 'rxjs/operators';
import {WebshopService} from '@app/shared/common/shop-system/services/webshop.service';

declare const abp: any; // Ensure "abp" global object is available

@Injectable({
    providedIn: 'root',
})
export class FieldAgentService {

    private webShopServiceProxy: WebShopServiceProxy;
    private webshopService: WebshopService;
    private userServiceProxy: UserServiceProxy;
    private virtulbankServiceProxy: VirtualBankServiceProxy;

    private _selectedManager: UserListDto | undefined = undefined;
    get selectedManager(): UserListDto | undefined {
        return this._selectedManager;
    }

    set selectedManager(value: UserListDto | undefined) {
        this._selectedManager = value;
        this.selectManager(value);
    }

    private _selectedCustomer: CustomerDto | undefined = undefined;
    get selectedCustomer(): CustomerDto | undefined {
        return this._selectedCustomer;
    }

    set selectedCustomer(value: CustomerDto | undefined) {
        this._selectedCustomer = value;
        this.selectCustomer(value);
    }

    // Observable for items which will be displayed
    private _selectedCustomer$: BehaviorSubject<CustomerDto>;
    private _customers$: BehaviorSubject<CustomerDto[]>;
    private _customerUsers$: BehaviorSubject<UserListDto[]>;
    private _customerUsersShort$: BehaviorSubject<UserListDto[]>;
    private _selectedManager$: BehaviorSubject<UserListDto>;

    vbaSubject: BehaviorSubject<VirtualBankAccountDto>;

    constructor(injector: Injector) {
        this.webShopServiceProxy = injector.get(WebShopServiceProxy);
        this.webshopService = injector.get(WebshopService);
        this.userServiceProxy = injector.get(UserServiceProxy);
        this.virtulbankServiceProxy = injector.get(VirtualBankServiceProxy);

        this._customers$ = new BehaviorSubject<CustomerDto[]>(undefined);
        this._customerUsers$ = new BehaviorSubject<UserListDto[]>(undefined);
        this._customerUsersShort$ = new BehaviorSubject<UserListDto[]>(undefined);
        this._selectedManager$ = new BehaviorSubject<UserListDto>(undefined);

        this._selectedCustomer$ = new BehaviorSubject<CustomerDto>(undefined);

        this.vbaSubject = new BehaviorSubject<VirtualBankAccountDto>(undefined);
    }

    //#region Vba
    get vba$(): Observable<VirtualBankAccountDto> {
        return this.vbaSubject.asObservable();
    }

    get vba(): VirtualBankAccountDto {
        return this.vbaSubject.getValue();
    }

    getVirtualBankAccountByUser$(id: number): Observable<VirtualBankAccountDto> {
        return this.virtulbankServiceProxy.getVirtualBankAccountByUser(id)
            .pipe(
                tap((result: VirtualBankAccountDto) => {
                    this.vbaSubject.next(result);
                })
            );
    }
    //#endregion Vba


    //#region Add Manager

    checkUserIfExist$(email: string): Observable<CheckUserIfExistOutputDto> {
        const input = {
            email: email
        } as ICheckIfUserExistInputDto;

        return this.userServiceProxy.checkIfUserExist(new CheckIfUserExistInputDto(input))
            .pipe(
                tap((result: CheckUserIfExistOutputDto) => {})
            );
    }

    addUserToCustomer$(
        name: string,
        surname: string,
        personalWorkEmail: boolean,
        emailAddress: string | undefined,
        phoneNumber: string | undefined
    ): Observable<UserListDto> {
        const input = {
            name: name,
            surname: surname,
            emailAddress: emailAddress,
            phoneNumber: phoneNumber,
            personalWorkEmail: personalWorkEmail,
            customerId: this.selectedCustomerId
        } as IAddUserToCustomerInputDto;

        return this.userServiceProxy.addUserToCustomer(new AddUserToCustomerInputDto(input))
            .pipe(
                tap((result: UserListDto) => {
                })
            );
    }

    //#endregion Add Manager

    //#region Managers
    selectManager(selectedManager: UserListDto): void {
        if (!this._selectedManager || this._selectedManager.id !== selectedManager.id) {
            this._selectedManager = selectedManager;
            this._selectedManager$.next(selectedManager);

            return;
        }

        this.unselectManager();
    }

    unselectManager(): void {
        this._selectedManager = undefined;
        this._selectedManager$.next(undefined);
    }

    //#endregion Managers

    //#region Customers

    // Observable for viewMode
    get customers$(): Observable<CustomerDto[]> {
        return this._customers$.asObservable();
    }

    setCustomers(result: PagedResultDtoOfCustomerDto): void {
        const customers = result.items;
        //update items
        this._customers$.next(customers);
    }

    getCustomers$(  filter: string | undefined
        , order: string | undefined
        , skip: number | 0
        , take: number | 12): Observable<PagedResultDtoOfCustomerDto> {

        return this.webShopServiceProxy.getAllCustomers(
            filter,
            order,
            skip,
            take
        )
            .pipe(
                tap((result: PagedResultDtoOfCustomerDto) => {
                    this.setCustomers(result);
                })
            );
    }

    getCustomerById$(id: number): Observable<CustomerDto> {
        return this.webShopServiceProxy.getCustomerById(id)
            .pipe(
                tap((result: CustomerDto) => {
                    this.selectCustomer(result);
                })
            );
    }

    //#endregion Customers

    //#region Selected Customer

    selectCustomer(selectedCustomer: CustomerDto): void {
        this._selectedCustomer = selectedCustomer;
        this._selectedCustomer$.next(selectedCustomer);
    }

    get selectedCustomerId(): number | undefined {
        return this._selectedCustomer?.id;
    }

    get selectedCustomer$(): Observable<CustomerDto> {
        return this._selectedCustomer$.asObservable();
    }

    // Unselect customer method
    unselectCustomer(): void {
        this._selectedCustomer = undefined;
        this._selectedCustomer$.next(undefined);
        this._customerUsers$.next(undefined);
        this._customerUsersShort$.next(undefined);
        this.unselectManager();
    }

    editCustomer$(input: ICreateOrUpdateVirtualBankDto): Observable<void> {
        return this.userServiceProxy.updateVirtualBankAccount(new CreateOrUpdateVirtualBankDto(input))
            .pipe(
                tap((result) => { })
            );
    }

    getCustomerForEdit$(input: number): Observable<VirtualBankAccountDto> {
        return this.userServiceProxy.getVirtualBankAccount(input)
            .pipe(
                tap((result: VirtualBankAccountDto) => { })
            );
    }

    //#endregion Selected Customer

    //#region Customer Users

    get customerUsers$(): Observable<UserListDto[]> {
        return this._customerUsers$.asObservable();
    }

    get customerUsersValue(): UserListDto[] {
        return this._customerUsers$.getValue();
    }

    get customerUsersShortValue(): UserListDto[] {
        return this._customerUsersShort$.getValue();
    }

    get isCustomerUsersAvailable(): boolean {
        return this.customerUsersValue && this.customerUsersValue.length > 0;
    }
    get isShowViewAllManagers(): boolean {
        return this.customerUsersValue && this.customerUsersValue.length > 5;
    }

    setCustomerUsers(result: PagedResultDtoOfUserListDto): void {
        const customerUsers = result.items;
        //update items
        this._customerUsers$.next(customerUsers);
        this._customerUsersShort$.next(this._deepCloneUsers(customerUsers, 5));
    }

    private _deepCloneUsers(users: UserListDto[], length: number = 5): UserListDto[] {
        const firstFiveUsersDeepClone = [];
        for (let i = 0; i < length; i++) {
            if (!users[i]) {
                break;
            }

            if (users[i]) {
                firstFiveUsersDeepClone.push(JSON.parse(JSON.stringify(users[i])));
            }
        }

        return firstFiveUsersDeepClone;
    }

    getAllCustomerUsers$(filter: string | undefined
        , sorting: string | undefined
        , skip: number | 0
        , take: number | 100): Observable<PagedResultDtoOfUserListDto> {

        return this.webShopServiceProxy.getAllCustomerUsers(
            filter,
            this.selectedCustomerId,
            sorting,
            skip,
            take
        )
            .pipe(
                tap((result: PagedResultDtoOfUserListDto) => {
                    this.setCustomerUsers(result);
                })
            );
    }

    //#endregion Customer Users

}

